"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();
  
  useEffect(() => {
    // Log detailed error information for debugging
    console.error("[NewsPage Error Boundary] Caught error:", {
      message: error.message,
      digest: error.digest,
      stack: error.stack,
      name: error.name,
      timestamp: new Date().toISOString(),
    });
  }, [error]);

  // Don't show error for notFound errors - let Next.js handle those
  if (error?.digest?.startsWith("NEXT_NOT_FOUND")) {
    return null;
  }

  const handleTryAgain = () => {
    // Use router.refresh() to reload the current route, which works better than reset() for server components
    router.refresh();
    // Also try reset as fallback
    try {
      reset();
    } catch (e) {
      // Ignore reset errors
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-[7.5%] bg-gray-50">
      <div className="text-center max-w-2xl">
        <h1 className="text-4xl font-bold mb-4 text-gray-900">Oops!</h1>
        <h2 className="text-2xl font-semibold mb-4 text-gray-700">
          We didn't find what you're looking for
        </h2>
        <p className="mb-6 text-gray-600">
          Something went wrong while loading this news article. This might be a temporary issue.
          Please try again or return to the news page.
        </p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={handleTryAgain}
            className="px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Try again
          </button>
          <a
            href="/news"
            className="px-6 py-3 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
          >
            Go to news page
          </a>
        </div>
      </div>
    </div>
  );
}
